import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Loader2, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import VipBadge from "@/components/app/VipBadge";

export default function Battle() {
  const [battle, setBattle] = useState(null);
  const [images, setImages] = useState({ a: null, b: null });
  const [loading, setLoading] = useState(false);
  const [voting, setVoting] = useState(false);
  const [winner, setWinner] = useState(null);
  const [error, setError] = useState(null);

  const loadBattle = useCallback(async () => {
    setLoading(true);
    setWinner(null);
    setError(null);
    try {
      const { data } = await api.get("/battle/random");
      setBattle(data);
      // fetch base64 images
      const [aImg, bImg] = await Promise.all([
        api.get(`/scan/${data.scan_a.id}/image`),
        api.get(`/scan/${data.scan_b.id}/image`),
      ]);
      setImages({ a: aImg.data.image_base64, b: bImg.data.image_base64 });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load battle");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBattle();
  }, [loadBattle]);

  const vote = async (winnerScanId) => {
    if (!battle || winner) return;
    setVoting(true);
    try {
      await api.post("/battle/vote", { battle_id: battle.battle_id, winner_scan_id: winnerScanId });
      setWinner(winnerScanId);
      toast.success("Vote locked in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vote failed");
    } finally {
      setVoting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="tape-label">MOG BATTLE ARENA</span>
          <h1 className="font-display text-5xl md:text-7xl mt-3 leading-none">
            WHO<br /><span className="text-[var(--primary)]">MOGS WHO?</span>
          </h1>
        </div>
        <div className="text-right">
          <Swords className="w-8 h-8 text-[var(--primary)] ml-auto" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">Cast your verdict</div>
        </div>
      </div>

      {loading && (
        <div className="panel p-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)] mx-auto" />
          <div className="mt-4 text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">Loading next battle...</div>
        </div>
      )}

      {error && (
        <div className="panel p-12 text-center">
          <div className="font-display text-3xl mb-3">{error}</div>
          <Link to="/scan" className="btn-brutal mt-4 inline-block" data-testid="battle-upload-link">
            Upload your scan →
          </Link>
        </div>
      )}

      {!loading && !error && battle && (
        <>
          <div className="grid grid-cols-12 gap-0 relative">
            <Fighter
              side="A"
              scan={battle.scan_a}
              image={images.a}
              onVote={() => vote(battle.scan_a.id)}
              disabled={voting || !!winner}
              isWinner={winner === battle.scan_a.id}
              isLoser={winner && winner !== battle.scan_a.id}
              testid="fighter-a"
            />
            {/* VS */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex-col items-center pointer-events-none">
              <div className="font-display text-7xl bg-[var(--bg)] border border-[var(--primary)] text-[var(--primary)] px-4 py-1">VS</div>
            </div>
            <Fighter
              side="B"
              scan={battle.scan_b}
              image={images.b}
              onVote={() => vote(battle.scan_b.id)}
              disabled={voting || !!winner}
              isWinner={winner === battle.scan_b.id}
              isLoser={winner && winner !== battle.scan_b.id}
              testid="fighter-b"
            />
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={loadBattle}
              className="btn-brutal"
              data-testid="next-battle-button"
              disabled={voting}
            >
              {winner ? "Next battle →" : "Skip battle →"}
            </button>
            <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
              <Zap className="inline w-3 h-3 text-[var(--primary)]" /> Votes update ELO globally
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const Fighter = ({ side, scan, image, onVote, disabled, isWinner, isLoser, testid }) => (
  <div
    className={`col-span-12 md:col-span-6 panel-bright relative overflow-hidden transition-all ${
      isWinner ? "border-[var(--success)]" : isLoser ? "opacity-40" : ""
    }`}
    data-testid={testid}
  >
    <div className="aspect-square relative">
      {image ? (
        <img src={`data:image/jpeg;base64,${image}`} alt={scan.username} className="w-full h-full object-cover grayscale-[20%]" />
      ) : (
        <div className="w-full h-full bg-[var(--surface)] animate-pulse" />
      )}
      <div className="absolute top-4 left-4 right-4 flex justify-between text-[10px] font-mono uppercase tracking-widest">
        <span className="bg-black/70 text-white px-2 py-1">FIGHTER {side}</span>
        <span className="bg-[var(--primary)] text-white px-2 py-1">SCORE {scan.overall_score}</span>
      </div>
      <div className="absolute bottom-4 left-4 right-4">
        <div className="font-display text-3xl text-white flex items-center gap-2 flex-wrap">
          @{scan.username}
          <VipBadge user={scan} size="sm" />
        </div>
      </div>
      {isWinner && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="font-display text-7xl text-[var(--success)]">MOGGER</div>
        </div>
      )}
      {isLoser && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <div className="font-display text-6xl text-[var(--primary)]">MOGGED</div>
        </div>
      )}
    </div>
    <button
      onClick={onVote}
      disabled={disabled}
      className="btn-brutal w-full rounded-none"
      data-testid={`vote-${side.toLowerCase()}`}
    >
      Vote Fighter {side} →
    </button>
  </div>
);
